import React, { useContext, useState } from 'react';
import { Container, Row, UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import OperationContext from '../context';
import { PageHeader, Section, SpinnerArea, Table, Button } from '../../../../shared/components';
import { OperationEsrrFilter, OperationEsrr } from '../../../../services/types/Esrr';
import useServiceReducer from '../../../../shared/hooks/useServiceReducer';
import esrrReducer from './esrrReducer';
import operEsrrService from '../../../../services/operEsrrService';
import { dateStr, isDraft, tooltipPreventOverflowModifiers } from '../../../../shared/misc/utils';
import { Cell } from '../../../../shared/components/Table';
import EsrrBadge from '../../EsrrBadge';
import Context from '../../../App/MainProvider/context';
import EsrrTooltip from './EsrrTooltip';
import EmptyHistory from './EmptyHistory';
import Role from '../../../../types/role';
import EsrrDeleteModal from './EsrrDeleteModal';
import NotificationContext from '../../../Notifications/NotificationContext';

const Esrr: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    operation: { id, number, esrr },
    reload: reloadOperation,
  } = useContext(OperationContext);
  const { notify, service, inSuperUserMode, inGuidanceServiceMode } = useContext(Context);
  const { reload: reloadNotifications } = useContext(NotificationContext);
  const [loading, setLoading] = useState(false);

  const { user } = useContext(Context);

  const canDelete = user.roleCode === Role.Admin || inSuperUserMode();

  const operationContext = useContext(OperationContext);
  const { operation } = operationContext;

  const [showDeleteFlowModal, setShowDeleteFlowModal] = useState(false);

  const canEdit =
    (operation.isMember || inSuperUserMode() || (operation.belongsGS && inGuidanceServiceMode())) &&
    !isDraft(operation);

  const initFilter: OperationEsrrFilter = {
    operationId: id,
    pageNumber: 1,
    sortField: 'status',
    sortDescending: false,
  };

  const [data, , , dispatch] = useServiceReducer(operEsrrService.getEsrr, esrrReducer, initFilter);

  const deleteFlow = (idEsrr: number) => {
    service(() => operEsrrService.deleteEsrr(idEsrr), {
      callback: () => {
        notify(
          t(`esrr`),
          t(`success-delete-remember-convergence-esrr`),
          'warning',
          'exclamation-triangle'
        );
        reloadOperation();
        reloadNotifications();
        navigate(`/operations/${number}/esrr`);
      },
      loader: setLoading,
    });
  };

  const currentVersion =
    data !== null && data.total > 0 ? data.list.filter((d) => d.id === data.list[0].id) : null;

  const currentVersionId = currentVersion && data ? data.list[0].id : null;

  const historyData =
    data !== null && data.total > 0 ? data.list.filter((d) => d.id !== data.list[0].id) : null;

  const changeSort = (column: string, descending: boolean) =>
    dispatch({ type: 'ORDER', column, descending });

  const download = (operationId: number, esrrId: number) => {
    service(() => operEsrrService.downloadDocument(operationId, esrrId), { loader: setLoading });
  };

  const downloadEzshare = (ezShareCode: string) => {
    window.open(`https://idbdocs.iadb.org/wsdocs/getdocument.aspx?docnum=${ezShareCode}`, '_blank');
  };

  const cells: Cell<OperationEsrr>[] = [
    {
      name: 'status',
      column: 'esrr',
      css: 'badge-bar-container',
      value: (x) => x.status,
      render: (_, obj) => (
        <div className="d-flex align-items-center">
          <EsrrBadge text={obj.status} />

          {obj.source === 'Legacy_Convergence' && (
            <span>
              <div className="ml-2 toolkit-tooltip" id={`tr-toolkit-id-${obj.id}`}>
                <FontAwesomeIcon icon="exclamation-circle" className="lastUpdateInfo" />
              </div>
              <UncontrolledTooltip
                className="tooltip-toolkit lastUpdateInfo"
                placement="bottom"
                target={`tr-toolkit-id-${obj.id}`}
                modifiers={tooltipPreventOverflowModifiers}
              >
                {t('esrrMigratedMsg')}
              </UncontrolledTooltip>
            </span>
          )}
        </div>
      ),
    },
    {
      column: '',
      render: (_, obj) => <EsrrTooltip x={obj} />,
    },
    {
      name: 'comment',
      css: 'esrr-rationale-container',
      column: 'Rationale',
      value: (x) => x.comment,
    },
    {
      name: 'createdBy',
      css: 'esrr-createdBy-container',
      column: 'Created By',
      value: (x) => x.createdBy,
    },
    {
      name: 'modifiedOn',
      css: 'esrr-createdOn-container',
      column: 'ESRR Date',
      value: (x) => (dateStr(x.modifiedOn) || t('na')) as string,
    },
    {
      column: 'Actions',
      render: (_, obj) => (
        <>
          {((obj.source === 'Legacy_Convergence' && obj.ezShareDocumentCode !== '') ||
            obj.source !== 'Legacy_Convergence') && (
            <div>
              <Button
                key={`tv_btn_download_${obj.id}`}
                className="btn-tertiary no-padding"
                icon="file-download"
                text={t('')}
                onClick={() =>
                  obj.source === 'Legacy_Convergence' && obj.ezShareDocumentCode !== ''
                    ? downloadEzshare(obj.ezShareDocumentCode)
                    : download(id, obj.id)
                }
              />
              {canDelete && obj.id === currentVersionId && (
                <>
                  <Button
                    htmlId="btnDelete"
                    text=""
                    onClick={() => setShowDeleteFlowModal(true)}
                    className="btn-tertiary no-padding"
                    icon="trash-alt"
                    disabled={obj.source === 'Legacy_Convergence'}
                  />
                  <UncontrolledTooltip
                    className="tooltip-impact"
                    placement="bottom"
                    target="btnDelete"
                    modifiers={tooltipPreventOverflowModifiers}
                  >
                    {t('Delete')}
                  </UncontrolledTooltip>
                </>
              )}
            </div>
          )}
        </>
      ),
    },
  ];

  return (
    <Container className="dashboard">
      <PageHeader title={t('esrrTitle', { number })} />
      <Row>
        <Section
          title={
            <>
              <h5>{data && data.total > 0 && t('currentVersion')}</h5>
              {canEdit && (
                <div>
                  {data && data.total > 0 && (
                    <Link
                      key="btnNewFromMostRecent"
                      className="btn btn-primary px-4 py-1"
                      to={`${location.pathname}/questions`}
                      state={{ fromLast: true }}
                    >
                      {t('esrrNewFromMostRecent')}
                    </Link>
                  )}
                  <Link
                    key="btnAdd"
                    className="btn btn-secondary px-4 py-1"
                    to={`${location.pathname}/questions`}
                    state={{ fromLast: false }}
                  >
                    {t('esrrNew')}
                  </Link>
                </div>
              )}
            </>
          }
        >
          {esrr === null && data && data.total === 0 && (
            <>
              <div className="m-3 d-flex justify-content-center">
                <div className="circle-base-big">
                  <div className="circle-inner">
                    <FontAwesomeIcon icon="file-word" />
                  </div>
                </div>
              </div>
              <div className="m-3 d-flex justify-content-center">
                <h1 className="page-title inner-title">{t('operationNoEsrr', { number })}</h1>
              </div>
            </>
          )}
          {currentVersion && data && (
            <div className="last-version-content">
              <hr />
              <div>
                <Table
                  data={currentVersion}
                  cells={cells}
                  keyFunc={(x) => x.id}
                  changeSort={changeSort}
                  onRowClick={(x) =>
                    navigate(`${location.pathname}/questions/${x.id}`, {
                      state: { fromLast: false },
                    })
                  }
                />
              </div>
            </div>
          )}
        </Section>
        {data && data.total > 0 && (
          <Section title={t('history')}>
            <SpinnerArea loading={loading}>
              {historyData && historyData.length > 0 ? (
                <>
                  <div className="history-content">
                    <hr />
                    <Table
                      data={historyData}
                      cells={cells}
                      keyFunc={(x) => x.id}
                      changeSort={changeSort}
                      onRowClick={(x) =>
                        navigate(`${location.pathname}/questions/${x.id}`, {
                          state: { fromLast: false },
                        })
                      }
                    />
                  </div>
                  {/*                   <TablePagination
                    pageSize={pageSize}
                    pageNumber={filter.pageNumber}
                    total={data.total}
                    onChangePage={changePage}
                  /> */}
                </>
              ) : (
                <EmptyHistory />
              )}
            </SpinnerArea>
          </Section>
        )}
      </Row>

      {canDelete && (
        <EsrrDeleteModal
          visible={showDeleteFlowModal}
          setVisible={setShowDeleteFlowModal}
          esrrId={data != null && data.list[0] != null ? data.list[0].id : 0}
          callback={deleteFlow}
        />
      )}
    </Container>
  );
};

export default Esrr;
